import * as Sentry from '@sentry/react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useForm } from '@tanstack/react-form'
import { useState } from 'react'
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Field,
  FieldError,
  Input,
  Label,
  Separator
} from '@repo/ui'
import { Building2, Mail, Pencil, Settings, Shield, UserRound } from 'lucide-react'
import { PageContent } from '@/components/PageContent'
import { PageHeader } from '@/components/PageHeader'
import { InfoField } from '@/components/InfoField'
import { UpdateProfileRequestSchema, apiUpdateProfile, apiUserOrgProfile } from '@/api/user/profile'
import { zodFieldValidator } from '@/common/parseDataType'
import type { ChangeEvent } from 'react'

export const Route = createFileRoute('/_auth/$orgSlug/profile/')({
  head: () => ({ meta: [{ title: 'My Profile - Snowvault' }] }),
  component: RouteComponent
})

function RouteComponent() {
  const router = useRouter()
  const { userOrgProfile } = Route.useRouteContext().user
  const [showEditDialog, setShowEditDialog] = useState(false)

  if (!userOrgProfile) return null

  const initials = `${userOrgProfile.first_name.charAt(0)}${userOrgProfile.surname.charAt(0)}`.toUpperCase()
  const fullName = `${userOrgProfile.first_name} ${userOrgProfile.surname}`.trim()
  const membershipRole = userOrgProfile.org.owner ? 'Owner' : 'Member'
  const permissionRole = userOrgProfile.org.admin ? 'Admin' : 'Editor'

  async function handleEditSuccess() {
    const updatedProfile = await apiUserOrgProfile()
    router.update({
      context: {
        ...router.options.context,
        user: { isAuthenticated: true, userOrgProfile: updatedProfile }
      }
    })
    await router.invalidate()
    setShowEditDialog(false)
  }

  return (
    <>
      <PageHeader
        breadcrumbs={[{ label: 'My Profile' }]}
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Profile settings">
                <Settings className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setShowEditDialog(true)}>
                <Pencil className="size-4" />
                Edit
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />
      <PageContent>
        <div className="max-w-xl space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-4">
                <Avatar className="size-14">
                  <AvatarFallback className="text-lg">{initials}</AvatarFallback>
                </Avatar>
                <div>
                  <CardTitle className="text-xl">{fullName}</CardTitle>
                  <p className="text-sm text-muted-foreground">{userOrgProfile.email}</p>
                </div>
              </div>
            </CardHeader>
            <Separator />
            <CardContent className="space-y-4">
              <InfoField icon={<Mail className="size-4" />} label="Email" value={userOrgProfile.email}>
                {userOrgProfile.email_verified ? (
                  <Badge variant="outline" className="border-green-600/30 text-green-600">
                    Verified
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-amber-600/30 text-amber-600">
                    Unverified
                  </Badge>
                )}
              </InfoField>
              <InfoField icon={<Building2 className="size-4" />} label="Organisation" value={userOrgProfile.org.name} />
              <InfoField icon={<UserRound className="size-4" />} label="Membership" value={membershipRole} />
              <InfoField icon={<Shield className="size-4" />} label="Role" value={permissionRole} />
            </CardContent>
          </Card>
        </div>
      </PageContent>

      <EditProfileDialog
        key={`${userOrgProfile.first_name}-${userOrgProfile.surname}`}
        open={showEditDialog}
        firstName={userOrgProfile.first_name}
        surname={userOrgProfile.surname}
        onClose={() => setShowEditDialog(false)}
        onSuccess={handleEditSuccess}
      />
    </>
  )
}

function EditProfileDialog({
  open,
  firstName,
  surname,
  onClose,
  onSuccess
}: {
  open: boolean
  firstName: string
  surname: string
  onClose: () => void
  onSuccess: () => Promise<void>
}) {
  const [submitError, setSubmitError] = useState<string | null>(null)

  const form = useForm({
    defaultValues: { first_name: firstName, surname },
    onSubmit: async ({ value }) => {
      setSubmitError(null)
      try {
        await apiUpdateProfile({ first_name: value.first_name, surname: value.surname })
        await onSuccess()
      } catch (err) {
        Sentry.captureException(err)
        setSubmitError(err instanceof Error ? err.message : 'An error occurred')
      }
    }
  })

  function handleClose() {
    form.reset()
    setSubmitError(null)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o: boolean) => {
        if (!o) handleClose()
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Profile</DialogTitle>
          <DialogDescription className="sr-only">Edit your profile information</DialogDescription>
        </DialogHeader>

        {submitError && <p className="text-sm text-destructive">{submitError}</p>}

        <form
          onSubmit={(e) => {
            e.preventDefault()
            void form.handleSubmit()
          }}
          className="space-y-4"
        >
          <form.Field
            name="first_name"
            validators={{ onChange: zodFieldValidator(UpdateProfileRequestSchema.shape.first_name) }}
          >
            {(field) => (
              <Field>
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={field.state.value}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={form.state.isSubmitting}
                />
                <FieldError errors={field.state.meta.errors.map((e) => ({ message: String(e) }))} />
              </Field>
            )}
          </form.Field>

          <form.Field
            name="surname"
            validators={{ onChange: zodFieldValidator(UpdateProfileRequestSchema.shape.surname) }}
          >
            {(field) => (
              <Field>
                <Label htmlFor="surname">Last Name</Label>
                <Input
                  id="surname"
                  value={field.state.value}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={form.state.isSubmitting}
                />
                <FieldError errors={field.state.meta.errors.map((e) => ({ message: String(e) }))} />
              </Field>
            )}
          </form.Field>

          <DialogFooter className="sm:justify-between">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={form.state.isSubmitting}>
              Cancel
            </Button>
            <form.Subscribe selector={(state) => state.isSubmitting}>
              {(isSubmitting) => (
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
